import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactElement, RefObject } from "react";
import type { Alignment, CompareMode, Unternehmen } from "@kundeninfo/api-client";
import { Arbeitsdialog, Icon, useSofortPopup } from "@kundeninfo/ui";
import { Eingabe, Hinweis, Werkzeuggruppe, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import {
  chooseAlignment, compareText, effectiveAlignment, freePeriod, grainOf, periodAround, periodText, shiftPeriod, toToday,
} from "./cockpitPeriod.js";
import type { CompareChoice, Period, PeriodKind } from "./cockpitPeriod.js";
import { COCKPIT_TEXT_WEB } from "./cockpitTexts.js";
import { Segments, pairs } from "./Segments.js";

/**
 * WERKZEUGLEISTE UND ZEILE ZEITRAUM (Spec „Werkzeugleiste und Zeitraum“). Die Leiste fängt keine Wahl ab, sie meldet nur; den Zustand führt
 * `Cockpit.tsx`. Nichts in beiden Zeilen wächst mit dem gewählten Wert: Die Ausrichtung bleibt gesperrt am Platz, der Fenstertext hat eine
 * feste Breite, und die Stufen der Leiste kommen allein von ihrer Breite (Hausregel „Leiste kippt nicht“).
 */

export type CockpitBarProps = {
  period: Period; compare: CompareChoice; company: "ALL" | string; companies: readonly Unternehmen[];
  phone: boolean; today: string; customizing: boolean; newTiles: number;
  /** Das Zahnrad, damit der Bereich ihm den Fokus geben kann, wenn der Auslöser mit seiner Wirkung verschwindet. */
  customizeRef?: RefObject<HTMLElement | null>;
  onPeriod: (p: Period) => void; onCompare: (c: CompareChoice) => void; onCompany: (c: "ALL" | string) => void;
  onCustomize: () => void; onPickTiles: () => void;
};

const T = COCKPIT_TEXT_WEB;
const ALL = "ALL";
const NO_COMPARE: CompareChoice = { mode: "NONE", align: null };

/** „frei“ (Übergang bis `Zeitraum` über W9): zwei Tage, 1 bis 1 096 Tage; „Übernehmen“ ist die Übernahme, eine Rückfrage braucht es nicht. */
function FreePeriodDialog(p: { period: Period; onApply: (next: Period) => void; onClose: () => void }): ReactElement {
  const [from, setFrom] = useState(p.period.from);
  const [to, setTo] = useState(p.period.to);
  const [invalid, setInvalid] = useState(false);
  const change = (set: (day: string) => void) => (d: { wert: string }): void => { set(d.wert); setInvalid(false); };
  const apply = (): void => {
    const next = freePeriod(from, to);
    if (next === null) setInvalid(true); else p.onApply(next);
  };
  return (
    <Arbeitsdialog titel={T.freeDialog.title} panel="cockpit-free-period" onAbbrechen={p.onClose}
      fuss={<>
        <KiKnopf art="leise" onClick={p.onClose}>{T.freeDialog.cancel}</KiKnopf>
        <KiKnopf art="primaer" onClick={apply}>{T.freeDialog.apply}</KiKnopf>
      </>}>
      <div className="ck__free">
        <Eingabe art="datum" beschriftung={T.freeDialog.from} wert={from} onGeaendert={change(setFrom)} />
        <Eingabe art="datum" beschriftung={T.freeDialog.to} wert={to} onGeaendert={change(setTo)} {...(invalid ? { fehler: T.freeDialog.rangeRule } : { hilfe: T.freeDialog.rangeRule })} />
      </div>
    </Arbeitsdialog>
  );
}

/** Der Hinweis als Knopf. Eigener Name: `me-hinweis` heißt von sich aus „Hinweis“, und das gäbe er dem Knopf. */
function NewTiles(p: { n: number; onClick: () => void }): ReactElement {
  const text = T.toolbar.newTiles(p.n);
  return <button type="button" className="ck__new" aria-label={text} onClick={p.onClick}><Hinweis>{text}</Hinweis></button>;
}

function Toolbar(p: CockpitBarProps): ReactElement {
  const { compare, period, customizing, customizeRef } = p;
  const customizePopup = useSofortPopup<HTMLElement>(customizing ? "" : T.toolbar.customize);
  const customizeButton = useCallback((element: HTMLElement | null) => {
    if (customizeRef !== undefined) customizeRef.current = element;
    customizePopup(element);
  }, [customizeRef, customizePopup]);
  const companyActive = p.company !== ALL;
  const compareActive = compare.mode !== "NONE";
  const grain = grainOf(period);
  return (
    <Werkzeugleiste className="ck__leiste" aria-label={T.toolbar.name} aktiv={Number(companyActive) + Number(compareActive)} zuruecksetzen="symbol"
      onZurueckgesetzt={() => { p.onCompany(ALL); p.onCompare(NO_COMPARE); }}
      filter={<>
        {p.companies.length > 1 && (
          <Werkzeuggruppe beschriftung={T.toolbar.company} aktiv={companyActive}>
            <Segments name={T.toolbar.company} value={p.company} onChoice={p.onCompany}
              options={[[ALL, T.toolbar.allCompanies], ...p.companies.map((c) => [c.id, c.shortCode] as const)]} />
          </Werkzeuggruppe>
        )}
        <Werkzeuggruppe beschriftung={T.toolbar.compare} aktiv={compareActive}>
          <Segments<"NONE" | CompareMode> name={T.toolbar.compare} value={compare.mode} options={pairs(T.toolbar.compareModes)}
            onChoice={(mode) => { p.onCompare({ ...compare, mode }); }} />
          <Segments<Alignment> name={T.toolbar.alignment} value={effectiveAlignment(compare, grain)} options={pairs(T.toolbar.alignments)} locked={!compareActive}
            onChoice={(align) => { p.onCompare(chooseAlignment(compare, align, grain)); }} />
        </Werkzeuggruppe>
      </>}
      // Der Träger ist für die Leiste EIN Werkzeug, darum hat er einen Namen: Die Pfeiltasten halten dort, Tab geht hinein. `data-symbol=""` hält die Telefonform davon ab,
      // das Zahnrad zu ihrem Hauptknopf zu machen. „Kacheln wählen …“ steht ab Tablet immer im Maß, außerhalb des Modus unsichtbar: Sonst fiele die Leiste beim Umschalten in eine
      // andere Stufe. Am Telefon gilt immer Stufe 3, die Zeile hat keinen Platz dafür: Dort steht es nur im Modus, statt des Hinweises, der in denselben Dialog führt.
      haupt={(
        <span className="ck__actions" role="group" aria-label={T.toolbar.tiles} data-symbol="">
          {(customizing || !p.phone) && <span className={customizing ? undefined : "ck__reserved"}><KiKnopf kompakt onClick={p.onPickTiles}>{T.toolbar.pickTiles}</KiKnopf></span>}
          {p.newTiles > 0 && !(p.phone && customizing) && <NewTiles n={p.newTiles} onClick={p.onPickTiles} />}
          <KiKnopf kompakt knopfRef={customizeButton} onClick={p.onCustomize} {...(customizing ? {} : { name: T.toolbar.customize })}>
            {customizing ? T.toolbar.customizeDone : <Icon name="gear" />}
          </KiKnopf>
        </span>
      )} />
  );
}

/** Ein offener Dialog, auch das Filterblatt im Schatten der Leiste. */
function anyDialogOpen(): boolean {
  type Sheet = HTMLElement & { offen?: boolean };
  return [...document.querySelectorAll<Sheet>("me-dialog, me-werkzeugleiste")]
    .some((n) => (n.localName === "me-dialog" ? n : n.shadowRoot?.querySelector<Sheet>("me-dialog"))?.offen === true);
}

function PeriodLine(p: CockpitBarProps): ReactElement {
  const { period, today, phone, onPeriod } = p;
  const { back } = period;
  const [freeOpen, setFreeOpen] = useState(false);
  const earlierPopup = useSofortPopup<HTMLElement>(T.period.earlier);
  const laterPopup = useSofortPopup<HTMLElement>(T.period.later);
  const { customizing } = p;
  const backPopup = useSofortPopup<HTMLElement>(customizing ? T.period.backHintPlain : T.period.backHint);
  const compareLine = compareText(period, p.compare);
  const comparePopup = useSofortPopup<HTMLElement>(compareLine);
  const nav = useRef<HTMLSpanElement>(null);
  const rest = useRef<HTMLSpanElement>(null);

  /** Der Rückweg verschwindet mit seiner Wirkung; hatte er den Fokus, geht der an „früher“, nicht an den `body`. */
  const goBack = useCallback((to: Period): void => {
    const hadFocus = rest.current?.querySelector("me-knopf")?.matches(":focus-within") === true;
    onPeriod(to);
    if (hadFocus) nav.current?.querySelector<HTMLElement>("me-knopf")?.focus();
  }, [onPeriod]);

  // Esc wirkt wie der Rückweg; ein Escape, das ein Dialog schon verbraucht hat (`preventDefault`, me-dialog), gehört dorthin. Steht der Fokus
  // auf dem `body`, kommt er am Dialog vorbei hier an: Darum sperrt jeder offene Dialog den Rückweg, auch das Filterblatt der Leiste. Im Modus Anpassen
  // gehört Esc der Wand (Griff, Menü, ihre Frage), der Knopf bleibt.
  useEffect(() => {
    if (back === null || freeOpen || customizing) return undefined;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape" && !e.defaultPrevented && !anyDialogOpen()) goBack(back); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); };
  }, [back, freeOpen, customizing, goBack]);

  /** Der laufende Zeitraum legt sich um heute, sonst zählt sein Ende; Hineinklicken ist damit vorbei. */
  const chooseKind = (kind: PeriodKind): void => {
    if (kind === "FREE") setFreeOpen(true);
    else onPeriod(periodAround(kind, period.from <= today && today <= period.to ? today : period.to));
  };
  // Ein Segment meldet nur eine Änderung: Steht „frei“ schon, öffnet erst der Klick auf sein Feld den Dialog wieder.
  const reopenFree = (e: MouseEvent): void => {
    if (e.nativeEvent.composedPath().some((n) => n instanceof HTMLInputElement && n.value === "FREE")) setFreeOpen(true);
  };
  return (
    <div className="ck__zeitraum">
      <span className="ck__kinds" onClick={reopenFree}>
        {/* Am Telefon „M | Q | J | frei“ als Kurztext der Segmente (C8); Name und Sofort-Popup bleiben „Monat“ usw. */}
        <Segments<PeriodKind> name={T.period.name} value={period.kind} options={pairs(T.period.kinds)} {...(phone ? { short: T.period.kindsShort } : {})}
          onChoice={chooseKind} />
      </span>
      <span ref={nav} className="ck__nav">
        <KiKnopf kompakt name={T.period.earlier} knopfRef={earlierPopup} onClick={() => { onPeriod(shiftPeriod(period, -1)); }}>{T.period.earlierGlyph}</KiKnopf>
        {!phone && <KiKnopf kompakt onClick={() => { onPeriod(toToday(period, today)); }}>{T.period.today}</KiKnopf>}
        <KiKnopf kompakt name={T.period.later} knopfRef={laterPopup} onClick={() => { onPeriod(shiftPeriod(period, 1)); }}>{T.period.laterGlyph}</KiKnopf>
      </span>
      <span className="ck__window" aria-live="polite">{periodText(period, phone)}</span>
      <span ref={rest} className="ck__rest">
        <span ref={comparePopup} className="ck__compare">{compareLine}</span>
        {back !== null && <KiKnopf kompakt art="leise" knopfRef={backPopup} onClick={() => { goBack(back); }}>{T.period.back(periodText(back, true))}</KiKnopf>}
      </span>
      {freeOpen && (
        <FreePeriodDialog period={period} onClose={() => { setFreeOpen(false); }} onApply={(next) => { setFreeOpen(false); onPeriod(next); }} />
      )}
    </div>
  );
}

export function CockpitBar(p: CockpitBarProps): ReactElement {
  return <><Toolbar {...p} /><PeriodLine {...p} /></>;
}
